/**
 * Whether a stored profile name is a person's name or just the email address it was minted from
 * (crossmarc/steward-app#2450, #2770). appcore sets `name` to the email's local part when sign-in
 * collected no given or family name (auth-triggers post-confirmation, post-authentication and
 * provision), so a "name" that is the local part is a login handle, never a name to show.
 */

const localPart = (email: string | null | undefined): string => (email ?? '').split('@')[0] ?? '';

/** Lower-cased, with the separators an email local part uses (`. - _ +` and spaces) folded to one space. */
export const foldedName = (value: string): string => value.toLowerCase().split(/[.\-_+\s]+/).filter(Boolean).join(' ');

/**
 * The trimmed candidate when it is a usable name, else `undefined`. Refused: empty, anything with
 * an "@", and exactly the email's local part (compared case-insensitively, nothing else folded,
 * because appcore mints the handle as exactly `email.split('@')[0]`). So "Jane Doe" is kept for
 * jane.doe@, where a real first.last address is common.
 */
export function usablePersonName(candidate: string | null | undefined, email: string | null | undefined): string | undefined {
  const value = typeof candidate === 'string' ? candidate.trim() : '';
  if (!value || value.includes('@')) return undefined;
  const local = localPart(email).trim().toLowerCase();
  if (local && value.toLowerCase() === local) return undefined;
  return value;
}

/** The same test with the separators an email local part uses folded ({@link foldedName}), for the welcome greeting (#2450), which refuses "Jane Doe" for jane.doe@ on purpose. */
export function usableFoldedPersonName(candidate: string | null | undefined, email: string | null | undefined): string | undefined {
  const value = usablePersonName(candidate, email);
  if (value === undefined) return undefined;
  const local = foldedName(localPart(email));
  return local && foldedName(value) === local ? undefined : value;
}

/** A name with the "@" check only (no local-part comparison), for a given and family name join. */
export function nameWithoutAddress(candidate: string | null | undefined): string | undefined {
  const value = typeof candidate === 'string' ? candidate.trim() : '';
  return value && !value.includes('@') ? value : undefined;
}
